import React from 'react';
import { useQuery } from '@apollo/client';
import {
  Chip,
  Link,
  Button,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
  Box,
  Stack,
} from '@mui/material';
import copyToClipboard from 'copy-to-clipboard';
import { enqueueSnackbar } from 'notistack';
import { useParams, Route, Routes } from 'react-router';
import { useNavigate } from 'react-router-dom';
import first from 'lodash/first';
import { getUIItem, setUIItem } from '@pv/common/utils';
import { PageLoading } from '@pv/common/components';
import { adminVenueQuery } from './graphql';
import {
  DangerPanel,
  DataImportsPanel,
  EventsPanel,
  InfoPanel,
  ProposalPaymentsChip,
  SquareChip,
  StripeChip,
  UsersPanel,
  MessagesPanel,
  FeatureFlagsPanel,
  MarketplacePanel,
  HubspotDealLink,
  PaymentsPanel,
  IntegrationsPanel,
  AiReplySessionsPanel,
} from './components';
import { MenuUpload } from './components/MenuUpload';
import { LegacyChip } from './components/Chips';
import { SubscriptionPanel } from './components/SubscriptionPanel';
import { LoginAsUserButton } from '../../components/LoginAsUserButton';

const tabKey = 'venue-tab';
const saveMostRecentlyVisitedTab = (tab) => {
  setUIItem(tabKey, tab);
};

const getMostRecentlyVisitedTab = () => {
  return getUIItem(tabKey, 'info');
};

export const Venue = () => {
  const theme = useTheme();

  const params = useParams();
  const navigate = useNavigate();
  const venueId = params.id;
  const variables = { id: venueId };
  const { data } = useQuery(adminVenueQuery, { variables });
  const venue = data?.adminVenue;
  const availablePlanSets = data?.adminPlanSets;

  const tab = params.tab;

  React.useEffect(() => {
    if (!tab) {
      const t = getMostRecentlyVisitedTab();
      navigate(`/admin/venues/${venueId}/${t}`);
    }
  }, [tab, navigate, venueId]);

  const onTabChange = (e, t) => {
    saveMostRecentlyVisitedTab(t);
    navigate(`/admin/venues/${venueId}/${t}`);
  };

  if (!venue) {
    return <PageLoading />;
  }

  const onClickIdChip = () => {
    copyToClipboard(venue.id);
    enqueueSnackbar(`Copied ${venue.id} to clipboard`, {
      variant: 'info',
    });
  };

  return (
    <div>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        gap={1}
        justifyContent="space-between"
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          padding={2}
          flexWrap="wrap"
        >
          <Chip
            clickable
            onClick={onClickIdChip}
            variant="outlined"
            label={`ID ${venue.id}`}
          />
          <Typography
            variant="h1"
            color={venue.discardedAt ? 'textSecondary' : 'textPrimary'}
          >
            Venue: {venue.discardedAt ? '(Archived) ' : ''} {venue?.name}
          </Typography>
          <StripeChip venue={venue} />
          <SquareChip venue={venue} />
          <ProposalPaymentsChip venue={venue} />
          <LegacyChip subscription={venue.subscription} />
        </Stack>
        <LoginAsUserButton user={first(venue.users)} />
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        style={{ marginBottom: theme.spacing(2) }}
        flexWrap="wrap"
      >
        <Typography variant="body1">
          Organization:{' '}
          <Link href={`/admin/organizations/${venue?.organization?.id}`}>
            {venue.organization?.name}
          </Link>
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          href={`/admin/organizations/${venue?.organization?.id}`}
        >
          Go to Org Admin Page
        </Button>

        {venue.stripeAccount && (
          <Button
            variant="outlined"
            color="primary"
            href={`https://dashboard.stripe.com/connect/accounts/${venue.stripeAccount.stripeAccountId}`}
            target="_blank"
            rel="noreferrer"
          >
            View connected Stripe account
          </Button>
        )}
        {venue.organization.hubspotDealId && (
          <HubspotDealLink dealId={venue.organization.hubspotDealId} />
        )}
      </Stack>
      <Paper>
        <Box
          sx={{
            flexGrow: 1,
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
          }}
        >
          <div style={{ flexShrink: 1 }}>
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={tab}
              onChange={onTabChange}
              sx={{
                borderRight: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Tab value="info" label="Info" />
              <Tab value="feature-flags" label="Feature Flags" />
              <Tab value="users" label="Users" />
              <Tab value="events" label="Events" />
              <Tab value="subscription" label="Subscription" />
              <Tab value="data_imports" label="Data Imports" />
              <Tab value="menu-upload" label="Menu Upload" />
              <Tab value="messages" label="Messages" />
              <Tab value="payments" label="Payment Processors" />
              <Tab value="marketplace" label="Marketplace" />
              <Tab value="integrations" label="Integrations" />
              <Tab value="ai-reply-sessions" label="AI Reply Sessions" />
              <Tab value="danger" label="Danger" />
            </Tabs>
          </div>
          <div style={{ width: '100%', flexGrow: 1, minHeight: 500 }}>
            <Routes>
              <Route
                path={'info'}
                index
                element={
                  <InfoPanel
                    venue={venue}
                    availablePlanSets={availablePlanSets}
                  />
                }
              />
              <Route
                path={'feature-flags'}
                element={<FeatureFlagsPanel venue={venue} />}
              />
              <Route path={'users'} element={<UsersPanel venue={venue} />} />
              <Route path={'events'} element={<EventsPanel venue={venue} />} />
              <Route
                path={'subscription'}
                element={<SubscriptionPanel venue={venue} />}
              />

              <Route
                path={'data_imports'}
                element={<DataImportsPanel venueId={venue.id} />}
              />
              <Route
                path="menu-upload"
                element={<MenuUpload venue={venue} />}
              />
              <Route
                path={'messages'}
                element={<MessagesPanel venueId={venue.id} />}
              />
              <Route
                path={'payments'}
                element={<PaymentsPanel venue={venue} />}
              />
              <Route
                path={'marketplace'}
                element={<MarketplacePanel venueId={venue.id} />}
              />
              <Route
                path={'integrations'}
                element={<IntegrationsPanel venue={venue} />}
              />
              <Route
                path={'ai-reply-sessions'}
                element={<AiReplySessionsPanel venueId={venue.id} />}
              />
              <Route path={'danger'} element={<DangerPanel venue={venue} />} />
            </Routes>
          </div>
        </Box>
      </Paper>
    </div>
  );
};

export default Venue;
